<template>
  <!-- 提出物承認申請画面 -->
  <CCard>
    <CCardHeader>
      <CRow>
        <CCol>
          <h2>自分の申請一覧</h2>
        </CCol>
      </CRow>
    </CCardHeader>
    <CCardBody>
      <p>承認済み以外の提出物一覧です。</p>
      <!-- ここに出るのは0件のときの案内だけ。失敗ではないので赤字にせず、ほかの一覧の0件の文と同じ案内の字（灰色）で出す。 -->
      <p class="text-muted" v-for="note in message" :key="note">{{ note }}</p>
      <!--
        承認者の候補を読み込んでいる最中に［申請］を押したときの知らせ（SA-8）。
        押した操作を止めた知らせなので、0件の案内（灰色）には混ぜず、気づける見た目で出す。
        利用者の入力の誤りではないので、誤りの赤字（下の preika-form-message）にもしない。
        この製品で注意に使っている帯（CAlert の warning）で出す。
      -->
      <CAlert v-if="approverLoadingNotice" color="warning" class="approver-loading-notice">
        {{ approverLoadingText }}
      </CAlert>
      <div v-if="items.length">
        <div class="table-responsive">
          ※複数プロジェクトに所属している場合は代表プロジェクト１つを表示しています。
          <table class="table table-bordered">
            <thead>
              <tr>
                <th style="text-align: center;" width="50">
                  <input type="checkbox" aria-label="全選択" v-model="selectAll" @change="toggleSelectAll" />
                </th>
                <th scope="col" width="80px" nowrap>ステータス</th>
                <th scope="col" width="100px" nowrap>提出物</th>
                <th scope="col" width="100px" nowrap>年月</th>
                <th scope="col" width="100px" nowrap>プロジェクト名</th>
                <th scope="col" width="100px" nowrap>承認者</th>
                <!--
                  **この列にも幅を書く。**ここだけ指定が無いと、全幅から
                  他の6列を引いた残りを丸ごと吸う。手元の 1600px で測ると
                  他6列の合計 646px に対して理由の列が 561px あり、理由が
                  空か短い行では右半分が空の罫線になっていた（テスターの
                  「左側に寄せすぎている」）。全列に幅があれば、余りは
                  比例配分されて各列へ散る。
                -->
                <th scope="col" width="200px" nowrap>差戻し理由</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in items" :key="item.id">
                <td style="text-align: center;">
                  <!-- 読み上げの名前は、その行の提出物と年月（無いと、値の「[object Object]」が読まれる）。 -->
                  <input type="checkbox" :aria-label="rowLabel(item)" :value="item" v-model="selectedItems" />
                </td>
                <td :style="statusColor(item.status)" nowrap>{{ item.status }}</td>
                <td nowrap>{{ item.document_name }}</td>
                <td nowrap>{{ item.year_month }}</td>
                <td nowrap>{{ item.project_name }}</td>
                <td nowrap>
                  <select
                    class="custom-select preika-control--person approver-select"
                    v-model="selectedNames[index]"
                    :class="{ 'is-invalid': approverMissingItems.includes(item) }"
                    :disabled="item.status === '承認待ち'"
                    @change="updateApprover(index)"
                  >
                    <!--
                      承認者が決まっていない行（初期値の無い行）に出す。
                      空の選択肢が無いと、欄は空白のままで、
                      候補が1人だけのとき、その人を選んでも選んだことにならない端末がある。
                      初期値（案件の責任者）や前に選んだ人が、承認者の候補にいない行も
                      同じ（2026-10-09）。
                    -->
                    <option :value="null" disabled>選択してください</option>
                    <!--
                      承認待ちの行で、承認者が候補（下の選択肢）にいないとき、その人の名前を出す
                      （2026-10-09）。欄は無効で変えられないが、選択肢に無い値だと空白の箱になり、
                      誰宛てに出したかが見えなかった（出したあとで、承認者が候補から外れた行が
                      当たる）。名前は、一覧の行が持っている承認者の名前。
                    -->
                    <option
                      v-if="fixedApproverName(item, index)"
                      :value="selectedNames[index]"
                    >
                      {{ fixedApproverName(item, index) }}
                    </option>
                    <option
                      v-for="option in options"
                      :value="option.id"
                      :key="option.id"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </td>
                <td class="linefeed">{{ item.comment }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!--
          ボタンを押して止めたときの文。数秒で消える（showButtonMessage）。
          **「承認者を選択してください。」だけは、欄の赤い枠が出ているあいだ出したままにする**
          （shownButtonMessages）。枠は選ぶまで残るのに文は4秒で消えていたので、少し経ってから
          見ると、赤い枠だけがあって何をすればよいかが出ていなかった（2026-10-11）。
        -->
        <CRow v-if="shownButtonMessages.length">
          <CCol>
            <p class="preika-form-message" v-for="error in shownButtonMessages" :key="error">{{ error }}</p>
          </CCol>
        </CRow>
        <CRow>
          <CCol>
            Slack用補足コメント ※Google Drive提出物との差異がある等、補足事項あれば記入して下さい。
            <CTextarea rows="5" v-model="sns_message"></CTextarea>
          </CCol>
        </CRow>
        <br/>
        <CRow class="justify-content-center">
          <CCol xs="auto">
            <div class="d-flex justify-content-center flex-wrap text-nowrap preika-action-row">
              <save-button
                v-if="!showButton"
                :label="'申請リストから除外'"
                :loading="loading"
                class="btn btn-danger"
                @click="excludeFromList"
              />
              <save-button
                v-if="!showButton"
                :label="'申請取消'"
                :loading="loading"
                class="btn btn-danger"
                @click="applyCancel"
              />
              <save-button
                v-if="!showButton"
                :label="'申請'"
                :loading="loading"
                class="btn btn-primary"
                @click="apply"
              />
            </div>
          </CCol>
        </CRow>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { rosterEnteredDayCount } from '@/utils/stampOriginFetch'
import SaveButton from "../common/SaveButton";
import axios from "axios";
import ConstList from "./../ConstList";
import SubmissionConst from "./ConstList";
import { resolveSubmissionApproverId, resolveSubmissionApproverSelections } from "./submissionApproverSelection";
import { submissionErrorMessage } from "./submissionErrorMessage";
export default {
  components: { SaveButton },
  name: "ReportList",
  data() {
    return {
      items: [],
      message: [],
      sns_message: "",
      buttonMessage: [], // ボタン押下イベントのメッセージ
      selectedItems: [], // 選択されたアイテムのIDを保持
      selectAll: false, // 全選択チェックボックスの状態
      showButton: false, // 申請ボタン・取消ボタンの表示非表示
      // 申請・取消・除外の通信中。3つのボタン（SaveButton）が見て、押せなくする。
      // data に無いと Vue が変化を追わず、通信中も押せて二重に申請できていた（2026-09-26）。
      loading: false,
      options: [],
      // 承認者の候補を読めたか。読めるまでは、初期値を候補で絞らない
      // （読む前に絞ると、どの行も「選択してください」になる）。
      optionsLoaded: false,
      // 承認者の候補を読めなかったときに出した知らせ。読めないまま［申請］を押したら、
      // 同じ知らせをもう一度出す（空なら、読めなかったことはない）。
      optionsError: "",
      // 承認者の候補を読み込んでいる最中に［申請］を押したので、「準備しています」の
      // 知らせ（SA-8）を出している。候補の通信が終わったら消す。
      approverLoadingNotice: false,
      approverLoadingText: SubmissionConst.MESSAGE_APPLY_APPROVER_LOADING,
      selectedNames: [],
      // ［申請］を、承認者が決まっていないために止めた行。欄に入力の誤りの枠を付ける。
      approverMissingItems: [],
    };
  },
  // DBから各種情報を取得
  mounted: function() {{
    axios
      .get(process.env.VUE_APP_API_ORIGIN + "/submission/list")
      .then(response => {
        this.items = response.data;

          // ステータス優先度マップ
        const statusPriority = {
          [ConstList.REJECT]: 4,          // 差戻し
          [ConstList.UNSUBMITTED]: 3,     // 未申請
          [ConstList.APPROVAL_PENDING]: 2,// 承認待ち
          [ConstList.APPROVAL]: 1         // 承認済み
        };

        // 降順ソート（priorityの大きいものが上に来る）
        this.items.sort((a, b) => {
          return (statusPriority[b.status] || 0) - (statusPriority[a.status] || 0);
        });

        this.selectedNames = resolveSubmissionApproverSelections(this.items, this.approverCandidatesFor);
        if (this.items.length === 0) {
          this.message.push(ConstList.EMPTY_OWN_REQUESTS);
          // データがない場合はボタンを非表示に設定
          this.showButton = true;
        } 
      })
      .catch(error => {
        alert(submissionErrorMessage(error));
      });
    }
    {
      axios
        .get((process.env.VUE_APP_API_ORIGIN) + '/submission/approvers')
        .then(response => {
          this.options = Array.isArray(response.data) ? response.data : []
          this.optionsLoaded = true
          // 一覧が先に読めていても後でも、候補が読めた時点で初期値を見直す
          // （候補にいない人が初期値の行を「選択してください」にする）。
          this.selectedNames = resolveSubmissionApproverSelections(this.items, this.approverCandidatesFor);
          // 読んでいる途中に［申請］を押して出した「準備しています」の知らせを消す。
          this.clearApproverLoadingMessage();
        })
        .catch(error => {
          this.clearApproverLoadingMessage();
          this.optionsError = submissionErrorMessage(error);
          alert(this.optionsError);
        })
    }
  },
  computed: {
    // ボタンの下に出す文。数秒で消える文（buttonMessage）に、承認者の欄に赤い枠が
    // 出ているあいだは「承認者を選択してください。」を足す（同じ文を2つは出さない）。
    shownButtonMessages: function() {
      const shown = this.buttonMessage.slice();
      if (this.approverMissingItems.length > 0 &&
          !shown.includes(SubmissionConst.ERROR_MESSAGE_APPLY_APPROVER)) {
        shown.push(SubmissionConst.ERROR_MESSAGE_APPLY_APPROVER);
      }
      return shown;
    },
  },
  methods: {
    // 申請
    apply: function() {
      // 1件も選択されていない場合
      if (this.selectedItems.length === 0) {
        this.showButtonMessage(SubmissionConst.ERROR_MESSAGE_APPLY, 3000);
        return;
      }
      // 選択したアイテムに承認待ちがあった場合
      const selectSatus = this.selectedItems.filter(
        item => item.status == ConstList.APPROVAL_PENDING);
      if(selectSatus.length > 0){
        this.showButtonMessage(SubmissionConst.ERROR_MESSAGE_APPLY_ALREADY, 4000);
        return;
      }
      // 申請する行は、ここで決める（確認の小窓を出しているあいだに選択が変わっても、
      // ここで確かめた行だけを送る）。
      const applyItems = this.selectedItems.slice();
      // 承認者の候補を読めていない・承認者が決まっていない行があれば、確認の小窓へ進まない。
      if (this.applyParamsFor(applyItems) === null) {
        return;
      }
    /*
     * 勤務表を申請するときは、手入力・修正した日（打刻のままでない日）の数を確認に出す。
     * 数え方は勤務表の画面と同じ（utils/stampOrigin.js）。読めなければ今までの文のまま。
     */
    const rosterMonths = this.selectedItems
      .filter(item => String(item.document_type ?? "").padStart(2, "0") === "01")
      .map(item => item.year_month);
    Promise.all(rosterMonths.map(yearMonth => rosterEnteredDayCount(yearMonth)))
      .then(counts => counts.reduce((sum, count) => sum + count, 0))
      .catch(() => 0)
      .then(enteredDays => this.$dialog
        .confirm(
          {
            title: "確認",
            body: enteredDays > 0
              ? `手入力・修正した日が${enteredDays}日あります。これらの変更内容は承認者にも共有されますが、提出してよろしいですか？`
              : "選択した提出物を申請しますか？"
          },
          {
            okText: "はい",
            cancelText: "キャンセル"
          }
        ))
        .then(() => {
          // **送る直前に、候補と承認者を確かめ直す。**確かめた値から、送る中身を作る
          // （小窓を出す前に作った中身を送らない。小窓のあいだに候補や欄が変わっていたら、
          // ここで止まる）。
          const params = this.applyParamsFor(applyItems);
          if (params === null) {
            return;
          }
          this.loading = true;
          axios
            .post(
              process.env.VUE_APP_API_ORIGIN + "/submission/register",params,
              {
                headers: {
                  // content-type指定。@requestBodyでサーバ側にてバインドするため。
                  "Content-Type": "application/json"
                }
              }
            )
            .then(() => {
              alert(SubmissionConst.APPLY_MESSAGE);
              window.location.reload();
            })
            .catch(error => {
              alert(submissionErrorMessage(error));
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(function() {
          // キャンセル
        });
    },
    // 申請取消
    applyCancel: function() {
      // 1件も選択されていない場合
      if (this.selectedItems.length === 0) {
        this.showButtonMessage(SubmissionConst.ERROR_MESSAGE_APPLY_CANCEL, 3000);
        return;
      }
      // 選択したアイテムに未申請または差戻があった場合
      const selectSatus = this.selectedItems.filter(
        item => item.status == ConstList.UNSUBMITTED || item.status ==  ConstList.REJECT);
      if(selectSatus.length > 0){
        this.showButtonMessage(SubmissionConst.ERROR_MESSAGE_APPLY_CANCEL_ALREADY, 4000);
        return;
      }
      const params = this.selectedItems.map(item => ({
        id: item.id,
        document_type: item.document_type,
        approver_user_id: item.approver_user_id === null ? item.user_id : item.approver_user_id,
        sns_message: this.sns_message,
        approval_type: item.approval_type,
        year_month: item.year_month,
      }));
      this.$dialog
        .confirm(
          {
            title: "確認",
            body: "選択した提出物の申請を取り消しますか？"
          },
          {
            okText: "はい",
            cancelText: "キャンセル"
          }
        )
        .then(() => {
          this.loading = true;
          axios
            .post(
              process.env.VUE_APP_API_ORIGIN + "/submission/unRegister",params,
              {
                headers: {
                  // content-type指定。@requestBodyでサーバ側にてバインドするため。
                  "Content-Type": "application/json"
                }
              }
            )
            .then(() => {
              alert(SubmissionConst.CANCEL_MESSAGE);
              window.location.reload();
            })
            .catch(error => {
              alert(submissionErrorMessage(error));
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(function() {
          // キャンセル
        });
    },
    // 申請除外
    excludeFromList: function() {
      if (this.selectedItems.length === 0) {
        this.showButtonMessage("提出物を選択してください。", 3000);
        return;
      }

      // 休暇関連（有給・代休・特休）は除外不可
      const isVacation = (it) => {
      const dt = String(it.document_type ?? "").padStart(2, "0"); // '5'→'05' に統一
      const name = String(it.document_name ?? "");
      return dt === "05" || /有給|代休|特休/.test(name);
    };
    if (this.selectedItems.some(isVacation)) {
      this.$dialog.alert(
        {
          title: "情報",
          body: "休暇関連の提出物はこの画面からは除外できません。休暇申請の一覧画面から削除してください。"
        },
        { okText: "確認" }
      );
      return; // ここで処理終了（除外しない）
    }

      const params = this.selectedItems.map(item => ({
        id: item.id,
        document_type: item.document_type,
        approver_user_id: item.approver_user_id === null ? item.user_id : item.approver_user_id,
        sns_message: this.sns_message,
        approval_type: item.approval_type,
      }));

      this.$dialog
        .confirm(
          {
            title: "確認",
            body: "選択した提出物を申請リストから除外してもよろしいですか？（再度個別に更新保存することで再リストされます）"
          },
          {
            okText: "はい",
            cancelText: "キャンセル"
          }
        )
        .then(() => {
          this.loading = true;
          axios
            .post(
              process.env.VUE_APP_API_ORIGIN + "/submission/exclude", params,
              { headers: { "Content-Type": "application/json" } }
            )
            .then(() => {
              alert("申請リストから除外しました。");
              window.location.reload();
            })
            .catch(error => {
              alert(submissionErrorMessage(error));
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(() => {
          // キャンセル時は何もしない
        });
    },
    // 承認状態の識別
    statusColor(status) {
      switch (status) {
        case ConstList.REJECT:
        case ConstList.UNSUBMITTED: 
          return { color: 'red' };
        case ConstList.APPROVAL_PENDING:
          return { color: 'orange' };
        case ConstList.APPROVAL:
          return { color: 'green' };
        default:
          return {};
      }
    },
    // ヘッダのチェックボックスを選択した場合
    toggleSelectAll() {
      if (this.selectAll) {
        // 全選択チェックボックスがチェックされた場合、全てのアイテムにチェック
        this.selectedItems = this.items.map(item => item);
      } else {
        this.selectedItems = [];
      }
    },
    // メッセージの表示時間設定
    // ボタンの下に文を出す。**すでに出ている同じ文は足さない**（続けて押すと、同じ文が
    // 押した数だけ重なって出ていた。2026-10-09）。消すまでの時間は、最後に押した時から数え直す。
    showButtonMessage: function(text, delay) {
      if (!this.buttonMessage.includes(text)) {
        this.buttonMessage.push(text);
      }
      this.messagehideDelay(delay);
    },
    messagehideDelay: function(delay) {
      clearTimeout(this.buttonMessageTimer);
      this.buttonMessageTimer = setTimeout(() => {
        this.buttonMessage = [];
      }, delay);
    },
    // チェック欄の読み上げの名前（その行の提出物と年月）。
    rowLabel: function(item) {
      return [item.document_name, item.year_month].filter(Boolean).join(" ");
    },
    // 承認待ちの行で、承認者が候補（選択肢）にいないときに出す名前。出さないときは空。
    // 未申請・差戻しの行では出さない（候補にいない人は「まだ選んでいない」のまま）。
    fixedApproverName: function(item, index) {
      if (!item || item.status !== ConstList.APPROVAL_PENDING) return "";
      const id = this.selectedNames[index];
      if (id === undefined || id === null) return "";
      if (this.options.some(option => option && String(option.id) === String(id))) return "";
      return item.approver ? String(item.approver) : "";
    },
    // 承認者変更の時
    updateApprover: function(index) {
      this.items[index].approver_user_id = this.selectedNames[index];
      // 選んだ行は、入力の誤りの枠を外す。
      this.approverMissingItems = this.approverMissingItems.filter(
        item => item !== this.items[index]);
    },
    // これから申請する行の承認者を確かめ、確かめた値から、送る中身を作る。
    // 進められないときは、理由を画面に出して null を返す。
    //   候補を読めていない … 新しい申請は進めない（読めるまで、初期値が候補にいるかを
    //     確かめられない）。読めなかったときは、そのときの知らせをもう一度出す。
    //     まだ読んでいる途中なら、少し待って押し直すよう、画面の上の知らせに出す。
    //   承認者が候補にいない行がある … 「まだ選んでいない行」として止める
    // 欄に前の承認者を見せるための決まり（下の approverCandidatesFor。候補を読めていない
    // あいだと承認待ちの行は絞らない）とは分ける。ここは、読めた候補そのもので見る。
    applyParamsFor: function(applyItems) {
      if (!this.optionsLoaded) {
        if (this.optionsError) {
          alert(this.optionsError);
          return null;
        }
        // まだ読んでいる途中。利用者の入力の誤りではないので、欄の枠は付けない。
        // 画面の上に、注意の帯で知らせる（出ているあいだは1つだけ）。候補の通信が終わったら消す。
        this.approverMissingItems = [];
        this.approverLoadingNotice = true;
        return null;
      }
      const approverIds = applyItems.map(
        item => resolveSubmissionApproverId(item, this.options));
      // 承認者が決まっていない行があれば、送る前に止める（サーバも同じ文で断る）。
      // 初期値や前に選んだ人が承認者の候補にいない行も、決まっていない行に数える。
      const noApprover = applyItems.filter((item, index) => approverIds[index] === null);
      // どの行で止まったかを、欄の枠で示す（止まらなかったときは枠を消す）。
      this.approverMissingItems = noApprover;
      if(noApprover.length > 0){
        this.showButtonMessage(SubmissionConst.ERROR_MESSAGE_APPLY_APPROVER, 4000);
        return null;
      }
      // 送る承認者は、いま確かめた値（行の値を、確かめずに送らない）。
      return applyItems.map((item, index) => ({
        id: item.id,
        document_type: item.document_type,
        approver_user_id: approverIds[index],
        sns_message: this.sns_message,
        approval_type: item.approval_type,
        year_month: item.year_month,
      }));
    },
    // 候補の通信が終わったら（読めても、読めなくても）、「準備しています」の知らせを消す。
    clearApproverLoadingMessage: function() {
      this.approverLoadingNotice = false;
    },
    // 行の承認者を絞るのに使う候補。絞らない行は undefined を返す。
    //   承認待ちの行 … 欄を固定して、申請したときの承認者をそのまま見せる（書き換えない）
    //   候補をまだ読めていない … 読めてから見直す
    approverCandidatesFor: function(item) {
      if (!this.optionsLoaded) return undefined;
      if (item && item.status === ConstList.APPROVAL_PENDING) return undefined;
      return this.options;
    },
  },
  watch: {
    selectedItems: function() {
      // 選択されたアイテムの数がテーブルのアイテム数と一致しない場合、全選択チェックボックスをfalseにする(外す)
      this.selectAll = this.selectedItems.length === this.items.length;
    }
  }
};
</script>

<style scoped>
td {
  word-break: break-all;
  vertical-align: middle;
  white-space: nowrap;
  padding-top: 0;
  padding-bottom: 0;
}

/* iOS Safariでも承認待ち中の選択済み承認者を読みやすく保つ。 */
.approver-select:disabled {
  color: var(--preika-gray-700, #495057);
  -webkit-text-fill-color: var(--preika-gray-700, #495057);
  opacity: 1;
}
</style>
